import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UtilityService } from '../utility.service';
import { JournalFormView } from 'app/models/general-ledger/JournalFormView';
import { environment } from 'environments/environment';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { FormSearch } from 'app/models/dashboard/FormStatus';

@Injectable({
	providedIn: 'root',
})
export class JournalEntryFormService {
	private readonly urlPath = `${environment.apiUrl}general-ledger/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/journal-entry`, request).pipe();
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient.put<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}`, request).pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}`).pipe();
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/forms/journal-entry/${formId}/docs/${docId}`);
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.urlPath}${clientId}/forms/journal-entry/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/forms/journal-entry/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/journal-entry/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getRecurringJournals(clientId: string): Observable<JournalFormView[]> {
		return this.httpClient
			.get<JournalFormView[]>(`${this.urlPath}${clientId}/forms/journal-entry/recurring-journals`)
			.pipe();
	}

	getPooledCashBalanceEntries(clientId: string, cashInBankAccount: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(
				`${this.urlPath}${clientId}/forms/journal-entry/pooledCashBalanceEntries/${cashInBankAccount}`,
				request
			)
			.pipe();
	}

	getJournalsForScheduling(clientId: string, data: any): Observable<JournalFormView[]> {
		return this.httpClient
			.post<JournalFormView[]>(`${this.urlPath}${clientId}/forms/journal-entry/manage/scheduling`, data)
			.pipe();
	}

	updateJournalForScheduling(clientId: string, message: any): Observable<JournalFormView[]> {
		return this.httpClient
			.post<JournalFormView[]>(`${this.urlPath}${clientId}/forms/journal-entry/manage/scheduling/update`, message)
			.pipe();
	}

	getReversalsAndCorrections(clientId: string, entryNumber: number): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/forms/journal-entry/reversals-corrections/${entryNumber}`)
			.pipe();
	}
}
